import moment from "moment";
import { useEffect, useState } from "react";

import MonitorService from "../../../api/MonitorService";
import { LoadingSpinner } from "../../../components/LoadingSpinner/LoadingSpinner";
import { SubMenuButton } from "../../../components/SubMenuButton/SubMenuButton";
import { TraplinkedReportModal } from "../../../components/TraplinkedReportModal/TraplinkedReportModal";
import traplinked_statuses from "../../../data/traplinked_statuses.json";
import styles from "../../../styles/customer-monitors.module.css";
import { DigitalMonitor } from "../../../types/monitor";

import { MonitorDetailsType } from "./Monitors";
import { ServiceDetailsModal } from "./ServiceDetailsModal";
import { ServiceInfo } from "./ServiceInfo";

type MonitorDetailsProps = {
  monitorDetails: MonitorDetailsType;
  lastServicesData: any[];
  lastServicesIncludes: any[];
  monitorProducts: any[];
  propertyAreaId?: string;
  documents: any[];
  loading: boolean;
  isFetching: boolean;
  isLoadingLastServices: boolean;
  deepLinkReportId?: string;
  deepLinkAction?: string;
  onDeepLinkHandled?: () => void;
};

const SUB_MENU_BUTTONS: string[] = ["LastServices", "AutomaticMessages"];

export const MonitorDetails = ({
  monitorDetails,
  lastServicesData = [],
  lastServicesIncludes = [],
  monitorProducts = [],
  propertyAreaId,
  documents = [],
  loading = false,
  isFetching = false,
  isLoadingLastServices = false,
  deepLinkReportId,
  deepLinkAction,
  onDeepLinkHandled,
}: MonitorDetailsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [typeOfAppointment, setTypeOfAppointment] = useState("");
  const [images, setImages] = useState<any>([]);
  const [currentTab, setCurrentTab] = useState<string>("");
  const [isTraplinkedModalOpen, setIsTraplinkedModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [reportCommentsMap, setReportCommentsMap] = useState<
    Record<string, number>
  >({});

  const [digitalMonitor, setDigitalMonitor] = useState<DigitalMonitor>({
    id: "",
    name: "",
    status: 0,
    statusText: "",
    type: 0,
    typeText: "",
    battery: 0,
    mac: "",
    lastHeartbeat: "",
    trap1: false,
    trap2: false,
    location: {
      name: "",
      address: "",
      path: "",
    },
  });

  useEffect(() => {
    setCurrentTab(
      monitorDetails?.wirelessId !== null ? "AutomaticMessages" : "LastServices"
    );
  }, [monitorDetails?.id, monitorDetails?.wirelessId]);

  useEffect(() => {
    setDigitalMonitor({
      id: "",
      name: "",
      status: 0,
      statusText: "",
      type: 0,
      typeText: "",
      battery: 0,
      mac: "",
      lastHeartbeat: "",
      trap1: false,
      trap2: false,
      location: {
        name: "",
        address: "",
        path: "",
      },
    });

    if (monitorDetails?.isWireless && monitorDetails?.wirelessId) {
      MonitorService.GetDigitalMonitor({ id: monitorDetails.wirelessId })
        .then((response) => {
          setDigitalMonitor(response.data);
        })
        .catch((error) => {
          console.error("Failed to fetch digital monitor:", error);
        });
    }
  }, [monitorDetails]);

  useEffect(() => {
    if (
      deepLinkAction !== "add_comment" ||
      !deepLinkReportId ||
      !monitorDetails?.reports?.length
    )
      return;

    const report = monitorDetails.reports.find(
      (r: any) => r.id === deepLinkReportId
    );

    if (report) {
      setSelectedReport(report);
      setIsTraplinkedModalOpen(true);

      onDeepLinkHandled?.();
    }
  }, [monitorDetails.reports, deepLinkReportId, deepLinkAction]);

  useEffect(() => {
    if (
      !Array.isArray(monitorDetails?.reports) ||
      Object.keys(reportCommentsMap).length > 0
    )
      return;

    const map: Record<string, number> = {};

    monitorDetails.reports.forEach((report: any) => {
      map[report.id] = report.comments?.length ?? 0;
    });

    setReportCommentsMap(map);
  }, [monitorDetails?.id]);

  const openModal = (service: any, type: string) => {
    setSelectedService(service);
    setTypeOfAppointment(type);
    setImages(
      documents?.filter((document) => document?.attachedTo?.id === service?.id)
    );
    setIsModalOpen(true);
  };

  const handleChangeTab = (tab: string) => {
    setCurrentTab(tab);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const openTraplinkedModal = (service: any) => {
    setSelectedReport(service);
    setIsTraplinkedModalOpen(true);
  };

  const currentStatus = traplinked_statuses.find(
    (item) => item.status === digitalMonitor.status
  );

  const includeDigitalReport = (type: string) => {
    return (
      type === "trap_triggered" ||
      type === "catch_detected" ||
      type === "false_triggering"
    );
  };

  const lastHeartbeat = moment(digitalMonitor.lastHeartbeat).format(
    "DD.MM.YYYY HH:mm"
  );

  return (
    <div id="monitor-details" className={styles["monitor-details"]}>
      {loading ? (
        <LoadingSpinner loading={loading} size={15}></LoadingSpinner>
      ) : (
        <>
          <p className={styles["monitor-details__p-bold"]}>
            {monitorDetails.name} {monitorDetails.isTemporary ? "T" : ""}
            {monitorDetails.number},{" "}
            <span className={styles["monitor-details__p"]}>
              {monitorDetails.location}
            </span>
          </p>

          {(monitorDetails.isWireless || monitorDetails.isTemporary) && (
            <div className={styles["buttons"]} style={{ gap: "10px" }}>
              {monitorDetails.isWireless && (
                <div className={styles["info-button"]}>Digital</div>
              )}
              {monitorDetails.isTemporary && (
                <div className={styles["info-button"]}>Temporär</div>
              )}
              {currentStatus && monitorDetails.isWireless && (
                <div
                  className={styles["info-button"]}
                  style={{ backgroundColor: currentStatus.color }}
                >
                  {currentStatus.type}
                </div>
              )}
            </div>
          )}

          {monitorDetails.isWireless && (
            <p style={{ color: "#3D5170", fontSize: 12 }}>
              <span>Seriennummer: {digitalMonitor.id}</span>
              <br />
              <span>
                Batteriestatus: {Math.round(digitalMonitor.battery * 100)}%
              </span>
              <br />
              <span>Last heartbeat: {lastHeartbeat}</span>
            </p>
          )}
          <div className={styles["loading-div"]}>
            <div>
              <p className={styles["gray-p"]}>Aktuelles Material:</p>
              {Array.isArray(monitorProducts) && monitorProducts.length > 0 ? (
                monitorProducts.map((product: any, index: number) => (
                  <div key={index} className={styles["materials-item"]}>
                    <p>{product.name}</p>
                    <p>{product.quantity}</p>
                  </div>
                ))
              ) : (
                <p>Keine Produkte verfügbar</p>
              )}
            </div>

            <div className={styles["buttons"]}>
              {SUB_MENU_BUTTONS.map((button, i) => (
                <SubMenuButton
                  currentTab={currentTab}
                  value={button}
                  onSetTab={() => handleChangeTab(button)}
                  key={i}
                  fontSize={12}
                />
              ))}
            </div>
            {isLoadingLastServices ? (
              <LoadingSpinner
                loading={isLoadingLastServices}
                size={75}
              ></LoadingSpinner>
            ) : (
              <div className={styles["list"]}>
                {currentTab === "LastServices" &&
                  (Array.isArray(lastServicesData) &&
                  lastServicesData.length > 0 ? (
                    lastServicesData.map((service: any, index: number) => (
                      <div
                        key={index}
                        onClick={() =>
                          openModal(
                            service,
                            lastServicesIncludes[index]?.appointment
                              ?.typeOfAppointment
                          )
                        }
                      >
                        <ServiceInfo
                          key={index}
                          service={service}
                          typeOfAppointment={
                            lastServicesIncludes[index]?.appointment
                              ?.typeOfAppointment
                          }
                        />
                      </div>
                    ))
                  ) : (
                    <p>Keine Kundenbesuche vorhanden</p>
                  ))}

                {currentTab === "AutomaticMessages" &&
                  (Array.isArray(monitorDetails.reports) &&
                  monitorDetails.reports.filter((report: any) =>
                    includeDigitalReport(report.type)
                  ).length > 0 ? (
                    [...monitorDetails.reports]
                      .sort(
                        (a, b) =>
                          new Date(b.localTimestamp).getTime() -
                          new Date(a.localTimestamp).getTime()
                      )
                      .filter((report: any) =>
                        includeDigitalReport(report.type)
                      )
                      .flatMap((report: any, index: number) => {
                        const serviceInfos = [];

                        serviceInfos.push(
                          <div key={`${index}-trap1`}>
                            <ServiceInfo
                              service={{ ...report }}
                              automaticMessages
                              showCommentButton
                              hasComments={
                                (reportCommentsMap[report.id] ?? 0) > 0
                              }
                              onCommentClick={openTraplinkedModal}
                            />
                          </div>
                        );

                        return serviceInfos;
                      })
                  ) : (
                    <p>Keine Automatische Meldungen vorhanden</p>
                  ))}

                <LoadingSpinner
                  loading={isFetching}
                  size={20}
                  top="10"
                ></LoadingSpinner>

                <ServiceDetailsModal
                  isOpen={isModalOpen}
                  onClose={closeModal}
                  monitorDetails={monitorDetails}
                  service={selectedService}
                  typeOfAppointment={typeOfAppointment}
                  documents={images}
                />

                {isTraplinkedModalOpen && selectedReport && (
                  <TraplinkedReportModal
                    isOpen={isTraplinkedModalOpen}
                    onClose={() => setIsTraplinkedModalOpen(false)}
                    monitorDetails={monitorDetails}
                    propertyAreaId={propertyAreaId}
                    service={selectedReport}
                    onCommentsUpdated={(count: number) => {
                      setReportCommentsMap((prev) => ({
                        ...prev,
                        [selectedReport.id]: count,
                      }));
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
